import React, {
  useEffect,
  useRef,
  useState,
  forwardRef,
  useImperativeHandle,
} from "react";
import Button from "@/common/elements/Button";
import Table from "@/common/elements/Table";
import CustomModal from "../CustomModal";
import { StyledGridCol, StyledGridRow } from "@/common/styles";
import { v4 as uuidv4 } from "uuid";
import dayjs from "dayjs";
import PropertyChainCustodyForm from "./PropertyChainCustodyForm";
import { hasCheckSecurity } from "@/util/common_function";
import { Entity_types, Security_Types } from "@/util/permission_bits";
import { LABELS_CAPITAL } from "@/util/usePermissionMask";
import {
  MODULES_LIST_WITH_ID,
  SUB_MODULES_LIST_WITH_ID,
} from "@/util/common_variable";

const PropertyChainCustodyDetails = forwardRef(
  ({ formData, onTransactionsUpdate }, ref) => {
    const userDetails = JSON.parse(localStorage.getItem("userDetails"));
    const [tableData, setTableData] = useState([]);
    const [selectedTransaction, setSelectedTransaction] = useState(null);
    const [showDetailModal, setShowDetailModal] = useState(false);
    const [editingTransaction, setEditingTransaction] = useState(null);
    const [newTransaction, setNewTransaction] = useState(null);
    const custodyFormRef = useRef();

    // Expose a method to open the modal with a pre-filled comment
    useImperativeHandle(ref, () => ({
      openModalWithComment: (data) => {
        setNewTransaction({ ...data, id: uuidv4() });
        setShowDetailModal(true);
      },
    }));

    useEffect(() => {
      if (formData && formData?.inventoryItemTransactions?.length) {
        const transactions = formData.inventoryItemTransactions;
        const mapped = transactions.map((t) => ({
          ...t,
          id: t.inventoryItemTransactionId
            ? t.inventoryItemTransactionId
            : uuidv4(),
          displayId: t.inventoryItemTransactionId
            ? t.inventoryItemTransactionId
            : "",
        }));
        // Sort mapped array by displayId (numeric sort)
        mapped.sort((a, b) => {
          const da = a.displayId ? Number(a.displayId) : 0;
          const db = b.displayId ? Number(b.displayId) : 0;
          return da - db;
        });
        setTableData(mapped);
      }
    }, [formData]);

    useEffect(() => {
      if (onTransactionsUpdate) {
        onTransactionsUpdate(tableData);
      }
    }, [tableData, onTransactionsUpdate]);

    const handleAdd = () => {
      setEditingTransaction(null);
      setShowDetailModal(true);
      setNewTransaction(null);
    };

    const handleEdit = (record = null) => {
      if(record.inventoryItemTransactionId) {
        setSelectedTransaction(record);
        setNewTransaction(null);
        setEditingTransaction(record);
        setShowDetailModal(true);
      }
      else {
        if (!selectedTransaction) {
          alert("Please select a transaction to edit.");
          return;
        }
        console.log(selectedTransaction);
        setNewTransaction(null);
        setEditingTransaction(selectedTransaction);
        setShowDetailModal(true);
      }
    };

    const handleDelete = () => {
      if (!selectedTransaction) {
        alert("Please select a transaction to delete.");
        return;
      }
      const newData = tableData.filter(
        (item) => item.id !== selectedTransaction.id
      );
      setTableData(newData);
      setSelectedTransaction(null);
    };

    const handleSaveTransaction = async () => {
      try {
        const values = await custodyFormRef.current.save();
        const dateAdded = values.dateAdded || dayjs().format("MM/DD/YYYY");
        const timeAdded = values.timeAdded || dayjs().format("HH:mm");

        const transaction = {
          ...values,
          inventoryItemTransactionId: values.inventoryItemTransactionId || null,
          dateAdded,
          timeAdded,
          user: values.user || "System",
          transactionType: values.transactionType || "Initial Entry",
          comments: values.comments || "",
          creatorStaffId: userDetails?.staffId,
        };

        let newData;
        if (editingTransaction) {
          newData = tableData.map((item) =>
            item.id === editingTransaction.id
              ? {
                  ...transaction,
                  id: editingTransaction.id,
                  displayId: editingTransaction.inventoryItemTransactionId
                    ? editingTransaction.inventoryItemTransactionId
                    : "",
                }
              : item
          );
        } else {
          newData = [
            ...tableData,
            { ...transaction, id: uuidv4(), displayId: "" },
          ];
        }
        console.log(newData);
        setTableData(newData);
        setShowDetailModal(false);
      } catch (error) {
        console.error("Failed to save transaction:", error);
      }
    };

    return (
      <>
        <StyledGridRow gutter={16} justify="space-between">
          <StyledGridCol xs={16}>
            <h6 style={{ margin: "0px" }}>Chain of Custody for this item</h6>
          </StyledGridCol>
          <StyledGridCol>
            {/* <Button style={{ marginBottom: "5px" }} onClick={handleAdd}>Add</Button> */}
            <Button
              style={{ marginLeft: 8, marginBottom: "5px" }}
              onClick={handleEdit}
              disabled={
                !hasCheckSecurity(
                  Security_Types.functionality,
                  Entity_types.subModule,
                  LABELS_CAPITAL.MODIFY,
                  MODULES_LIST_WITH_ID.MODULE_BOOKINGS,
                  SUB_MODULES_LIST_WITH_ID.Arrest_Report
                )
              }
            >
              Details
            </Button>
            {/* <Button style={{ marginLeft: 8, marginBottom: "5px" }} onClick={handleDelete} danger>Delete</Button> */}
          </StyledGridCol>
        </StyledGridRow>

        <Table
          headers={[
            { key: "displayId", text: "#" },
            { key: "user", text: "User" },
            { key: "dateAdded", text: "Date Added" },
            { key: "timeAdded", text: "Time Added" },
            { key: "transactionType", text: "Transaction Type" },
            { key: "comments", text: "Comments" },
          ]}
          data={tableData}
          onRowClick={(record) => {
            setSelectedTransaction(record);
          }}
          onRowDoubleClick={(record) => {
            handleEdit(record);
          }}
          selectedRowId={selectedTransaction ? selectedTransaction.id : null}
        />

        <CustomModal
          show={showDetailModal}
          size="xl"
          onHide={() => setShowDetailModal(false)}
          header="Custody Transaction Details"
          onSaveClick={handleSaveTransaction}
          onCloseClick={() => setShowDetailModal(false)}
        >
          <PropertyChainCustodyForm
            formData={
              editingTransaction
                ? [editingTransaction]
                : newTransaction
                ? [newTransaction]
                : []
            }
            ref={custodyFormRef}
          />
        </CustomModal>
      </>
    );
  }
);

export default PropertyChainCustodyDetails;
